import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { userService } from '../services/api';
import toast from 'react-hot-toast';

function Profile() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [profileForm, setProfileForm] = useState({
    username: '',
    email: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        username: user.username || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleProfileChange = (e) => {
    setProfileForm({
      ...profileForm,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Només s'envia el correu: el nom d'usuari és el subjecte del token i
      // canviar-lo tancaria la sessió en curs.
      await userService.updateProfile({ email: profileForm.email });
      toast.success(t('profile.profileUpdatedSuccess'));
      setEditing(false);
    } catch (error) {
      let message = t('profile.profileUpdateFailed');
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('profile.passwordsNotMatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error(t('profile.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await userService.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      toast.success(t('profile.passwordChangedSuccess'));
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
    } catch (error) {
      let message = t('profile.passwordChangeFailed');
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProfileForm({
      username: user?.username || '',
      email: user?.email || ''
    });
    setEditing(false);
  };

  const getRoleDisplayName = (role) => {
    return role === 'instructor' ? t('profile.instructor') : t('profile.analyst');
  };

  const getRoleBadgeType = (role) => {
    return role === 'instructor' ? 'primary' : 'success';
  };

  if (!user) return <div className="nordic-loading">{t('profile.loadingProfile')}</div>;

  // Els comptes d'equip entren amb un codi imprès: no tenen contrasenya ni
  // correu, i el que aquí seria «canviar la contrasenya» és, per a ells,
  // demanar una targeta nova a l'instructor.
  const esEquip = Boolean(user?.display_name) && !user?.email;

  return (
    <div className="nordic-profile">
      {/* Profile Header */}
      <div className="nordic-profile-header">
        <div className="nordic-profile-avatar">
          <div className="nordic-avatar-placeholder">
            {user.username?.charAt(0).toUpperCase()}
          </div>
        </div>
        <div className="nordic-profile-info">
          <h1 className="nordic-profile-name">{user.display_name || user.username}</h1>
          <div className="nordic-profile-meta">
            <span className={`nordic-badge nordic-badge-${getRoleBadgeType(user.role)}`}>
              {getRoleDisplayName(user.role)}
            </span>
            {user.email && (
              <span className="nordic-profile-email">{user.email}</span>
            )}
          </div>
        </div>
      </div>

      {/* Profile Information Card */}
      <div className="nordic-card">
        <div className="nordic-card-header">
          <h2 className="nordic-card-title">{t('profile.profileInformation')}</h2>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="nordic-btn nordic-btn-secondary"
            >
              {t('profile.editProfile')}
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleProfileSubmit} className="nordic-profile-form">
            <div className="nordic-form-group">
              <label className="nordic-form-label">{t('profile.username')}</label>
              <div className="nordic-form-static">
                <strong>{user.username}</strong>
                <small className="nordic-form-help">{t('profile.usernameCannotBeChanged')}</small>
              </div>
            </div>

            <div className="nordic-form-group">
              <label className="nordic-form-label">{t('profile.email')}</label>
              <input
                type="email"
                name="email"
                value={profileForm.email}
                onChange={handleProfileChange}
                className="nordic-form-input"
                required
              />
            </div>

            <div className="nordic-form-group">
              <label className="nordic-form-label">{t('profile.role')}</label>
              <div className="nordic-form-static">
                <span className={`nordic-badge nordic-badge-${getRoleBadgeType(user.role)}`}>
                  {getRoleDisplayName(user.role)}
                </span>
                <small className="nordic-form-help">{t('profile.roleCannotBeChanged')}</small>
              </div>
            </div>

            <div className="nordic-form-actions">
              <button
                type="submit"
                className="nordic-btn nordic-btn-primary"
                disabled={loading}
              >
                {loading ? t('profile.saving') : t('profile.saveChanges')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="nordic-btn nordic-btn-ghost"
                disabled={loading}
              >
                {t('profile.cancel')}
              </button>
            </div>
          </form>
        ) : (
          <div className="nordic-profile-display">
            <div className="nordic-profile-field">
              <label className="nordic-profile-field-label">{t('profile.username')}</label>
              <div className="nordic-profile-field-value">{user.username}</div>
            </div>

            <div className="nordic-profile-field">
              <label className="nordic-profile-field-label">{t('profile.email')}</label>
              <div className="nordic-profile-field-value">{user.email || t('profile.noEmailSet')}</div>
            </div>

            <div className="nordic-profile-field">
              <label className="nordic-profile-field-label">{t('profile.role')}</label>
              <div className="nordic-profile-field-value">
                <span className={`nordic-badge nordic-badge-${getRoleBadgeType(user.role)}`}>
                  {getRoleDisplayName(user.role)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Settings Card
          Un equip entra amb un codi imprès i no té contrasenya: aquesta targeta
          li oferiria un formulari que el servidor rebutja amb un 400. Es
          reparteix targeta nova des de la pantalla d'equips, no des d'aquí. */}
      {!esEquip && (
      <div className="nordic-card">
        <div className="nordic-card-header">
          <h2 className="nordic-card-title">{t('profile.securitySettings')}</h2>
          {!showPasswordForm && (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="nordic-btn nordic-btn-secondary"
            >
              {t('profile.changePassword')}
            </button>
          )}
        </div>

        {showPasswordForm ? (
          <form onSubmit={handlePasswordSubmit} className="nordic-profile-form">
            <div className="nordic-form-group">
              <label className="nordic-form-label">{t('profile.currentPassword')}</label>
              <input
                type="password"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                className="nordic-form-input"
                required
              />
            </div>

            <div className="nordic-form-group">
              <label className="nordic-form-label">{t('profile.newPassword')}</label>
              <input
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                className="nordic-form-input"
                required
                minLength="6"
              />
              <small className="nordic-form-help">{t('profile.passwordMinLength')}</small>
            </div>

            <div className="nordic-form-group">
              <label className="nordic-form-label">{t('profile.confirmNewPassword')}</label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                className="nordic-form-input"
                required
              />
            </div>

            <div className="nordic-form-actions">
              <button
                type="submit"
                className="nordic-btn nordic-btn-primary"
                disabled={loading}
              >
                {loading ? t('profile.changingPassword') : t('profile.changePassword')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                className="nordic-btn nordic-btn-ghost"
                disabled={loading}
              >
                {t('profile.cancel')}
              </button>
            </div>
          </form>
        ) : (
          <div className="nordic-profile-display">
            <div className="nordic-profile-field">
              <label className="nordic-profile-field-label">{t('profile.password')}</label>
              <div className="nordic-profile-field-value">••••••••</div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export default Profile;